import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Radios, Button, Fieldset, ErrorSummary } from 'govuk-react-jsx';
import {
  setAccountTypeSelected,
  getAccountTypeSelected,
} from '../features/registration/registrationJourneySlice';
import {
  getCurrentLanguage,
  getCopyProviderCY,
  getCopyProviderEN,
} from '../features/app/appSlice';
import { chooseAccountType, signInRegistration } from '../firebase';
import useGaStartEvent from '../hooks/useGaStartEvent';
import useTitle from '../hooks/useTitle';

const AccountType = () => {
  const accountTypeSelectedRedux = useSelector(getAccountTypeSelected);
  const history = useHistory();
  const dispatch = useDispatch();
  const [hasError, setHasError] = useState(false);
  const [errorList, setErrorList] = useState([]);
  const currentLanguage = useSelector(getCurrentLanguage);
  const copyProviderCY = useSelector(getCopyProviderCY);
  const copyProviderEN = useSelector(getCopyProviderEN);
  const copyProvider =
    currentLanguage === 'en' ? copyProviderEN : copyProviderCY;
  useTitle(
    'landlordReg.AccountType.s11',
    'What type of account do you need? - OZEV Electric Vehicle Chargepoint Grant - GOV.UK'
  );

  const handleSelection = (event) => {
    dispatch(setAccountTypeSelected(event.target.value));
  };

  const legendTitle = copyProvider.getCopy(
    'landlordReg.AccountType.s1',
    'What type of account do you need?'
  );

  const errorMessage = copyProvider.getCopy(
    'landlordReg.AccountType.s2',
    'Select a User'
  );

  useGaStartEvent('LandlordRegistration', 'reg_account_type');

  const accountType = () => {
    chooseAccountType({ accountType: accountTypeSelectedRedux })
      .then(() => {
        setHasError(false);
        if (accountTypeSelectedRedux === 'Property Management Company') {
          history.push('/pmcReg/create-account');
        } else {
          history.push('/landlordReg/create-account');
        }
      })
      .catch(() => {
        setHasError(true);
        setErrorList([{ children: errorMessage, href: '#Landlord' }]);
      });
  };

  const newRegistration = () => {
    signInRegistration()
      .then(() => {
        accountType();
      })
      .catch(() => {
        setHasError(true);
        setErrorList([{ children: errorMessage, href: '#Landlord' }]);
      });
  };

  const handleBtnClick = () => {
    if (!accountTypeSelectedRedux) {
      setErrorList([{ children: errorMessage, href: '#Landlord' }]);
      setHasError(true);
    } else if (accountTypeSelectedRedux === 'Installer') {
      history.push('/installer/installer-account-guidance');
    } else {
      newRegistration();
    }
  };

  const pageModel = (
    <>
      {errorList?.length > 0 && (
        <ErrorSummary
          errorList={errorList}
          titleChildren={copyProvider.getCopy(
            'Common.error-summery',
            'There is a problem'
          )}
        />
      )}
      <Fieldset
        legend={{
          children: legendTitle,
          className:
            'govuk-fieldset__legend govuk-fieldset__legend--xl govuk-!-margin-bottom-7',
          isPageHeading: true,
        }}
      >
        <Radios
          errorMessage={
            hasError && {
              children: errorMessage,
            }
          }
          idPrefix="convention"
          name="convention"
          value={accountTypeSelectedRedux}
          onChange={handleSelection}
          items={[
            {
              children: (
                <span key="0" className="govuk-!-margin-bottom-1">
                  {copyProvider.getCopy(
                    'landlordReg.AccountType.s3',
                    'Landlord'
                  )}
                </span>
              ),
              hint: {
                children: (
                  <span key="0">
                    {copyProvider.getCopy(
                      'landlordReg.AccountType.s4',
                      'You are applying on behalf of yourself for example, a company of leaseholders applying for the property you rent as a Right to manage company (RTM), a freeholder applying for the property you own, or a property factor.'
                    )}
                  </span>
                ),
              },
              value: 'Landlord',
              id: 'Landlord',
            },
            {
              children: (
                <span key="0" className="govuk-!-margin-bottom-1">
                  {copyProvider.getCopy(
                    'landlordReg.AccountType.s5',
                    'Property management company'
                  )}
                </span>
              ),
              hint: {
                children: (
                  <span key="0">
                    {copyProvider.getCopy(
                      'landlordReg.AccountType.s6',
                      'You are the representative or managing agent of one or more landlords applying on behalf of your clients.'
                    )}
                  </span>
                ),
              },
              value: 'Property Management Company',
              id: 'Pmc',
            },
            {
              children: (
                <span key="1" className="govuk-!-margin-bottom-1">
                  {copyProvider.getCopy(
                    'landlordReg.AccountType.s7',
                    'Installer'
                  )}
                </span>
              ),
              hint: {
                children: (
                  <span key="1">
                    {copyProvider.getCopy(
                      'landlordReg.AccountType.s8',
                      'You are the installer whose company is responsible for installing the chargepoint.'
                    )}
                  </span>
                ),
              },
              value: 'Installer',
              id: 'Installer',
            },
          ]}
        />
      </Fieldset>
      <Button className="govuk-button" onClick={handleBtnClick}>
        {copyProvider.getCopy('landlordReg.AccountType.s9', 'Continue')}
      </Button>
    </>
  );

  return pageModel;
};

export default AccountType;
